import * as _ from 'lodash';
import * as moment from 'moment';
import { FormEvent, useMemo, useState } from 'react';
import { ColDef, ColumnApi, FirstDataRenderedEvent, GridApi, GridReadyEvent, ICellRendererParams, IHeaderParams, SortChangedEvent, ValueGetterParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import RankRenderer from '@nkr/views/admin/center/list/common/rank-renderer';
import {Button, Col, FormControl, Modal, Row} from 'react-bootstrap';
import { DetailReportWrapper } from './styles';
import {IAccountsPayable, IAccountsPayableDetail} from '@nkr/models/index';
import CcbPayNowForm from "@nkr/views/reports/accounts-payable/pay-now";

interface IProps {
    show: boolean,
    centerSummary: IAccountsPayable,
    data: IAccountsPayableDetail[],
    onClose: () => void,
    onDetailChecked: ( detail : IAccountsPayableDetail ) => void,
    onCheckPayNow: () => void,
    onMarkPaid: ( achConfirmation : string, amountPaid : number, lineItems : IAccountsPayableDetail[] ) => void
}

const ApDetail = (props: IProps) => {
    const [gridApi, setGridApi] = useState<GridApi>(null);
    const [columnApi, setColumnApi] = useState<ColumnApi>(null);
    const [billingType, setBillingType] = useState('');

    const onGridReady = (e: GridReadyEvent) => {
        setGridApi(e.api);
        setColumnApi(e.columnApi);

        e.columnApi.applyColumnState({
            state: [
                { colId: 'centerHasLatePaymentsInLastYear', sort: 'asc' }
            ]
        });
    };

    const onFirstDataRendered = (e: FirstDataRenderedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const handleBillingTypeChange = (e: FormEvent<FormControl & HTMLSelectElement>) => {
        setBillingType(e.currentTarget.value);
    };

    const createTopData = (): IAccountsPayableDetail[] => {
        let result = {
            isChecked: false,
            amount: 0,
            swapNumber: '',
            donorAlias: '',
            billingType: '',
            billableCenterAlias: '',
            billableCenterId: 0,
            recipientAlias: '',
            invoiceDate: '',
            daysOutstanding: 0,
            payableCenterId: 0,
            payableCenterAlias: '',
            isPaid: false
        };

        //get transportCost Total
        result.amount = props.data.reduce((a, v) => a = a + v.amount, 0);
        result.daysOutstanding = Math.round(_.sumBy(props.data, x => x.daysOutstanding) / props.data.length);

        return [result];
    };

    const columns: ColDef[] = [
        {
            colId: 'isChecked',
            headerName: 'C',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>C</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                if (params.data.swapNumber != '') {
                    return (
                        <input type="checkbox" checked={params.data.isChecked} onClick={() => {props.onDetailChecked(params.data)}} />
                    );
                }

                return null;
            },
            width: 50,
            suppressSizeToFit: true,
            sortable: false,
        },

        {
            colId: 'rank',
            headerName: 'Rank',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>R</SortableHeaderComponent>,
            pinnedRowCellRendererFramework: (params: ICellRendererParams) => 'Totals:',
            cellRendererFramework: (params: ICellRendererParams) => {
                if (params.data.swapNumber != '')
                    return (
                        <RankRenderer {...params} />
                    );

                return null;
            },
            width: 50,
            suppressSizeToFit: true,
            sortable: false,
            valueGetter: (params: ValueGetterParams) => params.node.rowIndex + 1
        },

        {
            colId: 'billingType',
            field: 'billingType',
            headerName: 'billingType',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Type</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                if (params.data.swapNumber != '') {
                    return <>{params.data.billingType}</>
                } else {
                    return 'Totals'
                }
            },
            width: 100,
            suppressSizeToFit: true
        },

        {
            colId: 'swapNumber',
            field: 'swapNumber',
            headerName: 'Swap #',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Swap #</SortableHeaderComponent>,
            width: 100,
            suppressSizeToFit: true
        },

        {
            colId: 'donorAlias',
            field: 'donorAlias',
            headerName: 'Donor',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Donor</SortableHeaderComponent>,
            width: 100,
            suppressSizeToFit: true
        },

        {
            colId: 'recipientAlias',
            field: 'recipientAlias',
            headerName: 'Recipient',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Recipient</SortableHeaderComponent>,
            width: 100,
            suppressSizeToFit: true
        },

        {
            colId: 'invoiceDate',
            field: 'invoiceDate',
            headerName: 'invoiced',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Invoiced</SortableHeaderComponent>,
            width: 100,
            suppressSizeToFit: true
        },

        {
            colId: 'daysOutstanding',
            field: 'daysOutstanding',
            headerName: 'Days O/S',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Days O/S</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                if (params.data.swapNumber != '') {
                    return params.value;
                }

                return '~' + params.value;
            },
            width: 70
        },

        {
            colId: 'amount',
            field: 'amount',
            headerName: 'Amount',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Amount</SortableHeaderComponent>,
            width: 100,
            sortingOrder: ['desc', 'asc'],
            cellRendererFramework: (params: ICellRendererParams) => params.data.amount.toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        },

        {
            colId: 'billableCenterAlias',
            field: 'billableCenterAlias',
            headerName: 'Billable Center',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Billable Center</SortableHeaderComponent>,
            width: 130,
            sortingOrder: ['desc', 'asc']
        },

        {
            colId: 'isPaid',
            field: 'isPaid',
            headerName: 'Paid',
            pinnedRowCellRendererFramework: (params: ICellRendererParams) => '',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Paid</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => params.data.isPaid ? <span className='label label-success'>Y</span> : '-',
            width: 100,
            sortingOrder: ['desc', 'asc']
        },

        {
            colId: 'invoiceDate',
            field: 'invoiceDate',
            headerName: 'Invoice Date',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Invoice Date</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => moment(params.data.invoiceDate).isValid() ? moment(params.data.invoiceDate).format('MM/DD/YYYY') : '--',
            width: 110,
            sortingOrder: ['desc', 'asc']
        },
    ];

    const typeList = _.uniq(props.data.map(x => x.billingType));

    const filteredData = props.data.filter(x => {
        let billingTypeFilter = billingType != '' ? x.billingType == billingType : true;
        return billingTypeFilter;
    });

    const columnDefs = useMemo(() => columns, []);
    const rowData = useMemo(() => filteredData, [filteredData]);

    return (
        <Modal backdrop='static' className='modal-xxl' bsSize='lg' show={props.show} onHide={props.onClose}>
            <Modal.Header closeButton>
                <Modal.Title>Payable Center: {props.centerSummary?.centerAlias}</Modal.Title>
            </Modal.Header>

            <Modal.Body >
                <Row className="row-margined">
                    <Col xs={6} sm={2}>
                        <FormControl
                            bsSize='sm'
                            componentClass='select'
                            value={billingType}
                            onChange={handleBillingTypeChange}
                        >
                            <option value=''>- All Types -</option>
                            {typeList.map((t, i) =>
                                <option key={'type-' + i} value={t}>{t}</option>
                            )}
                        </FormControl>
                    </Col>
                    <Col xs={6} sm={10}>
                        <button className="btn btn-primary pull-right" type="button" onClick={props.onCheckPayNow}>Check Pay Now</button>
                    </Col>
                </Row>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 5 }}>
                    <div>Type:&nbsp;</div>
                    <div>
                    </div>
                </div>

                <DetailReportWrapper style={{ height: 500 }}>
                    <AgGridReact
                        defaultColDef={{
                            headerClass: 'list-header',
                            cellClass: 'list-cell',
                            sortable: true
                        }}
                        columnDefs={columnDefs}
                        rowData={rowData}
                        suppressRowClickSelection={false}
                        suppressCellSelection={true}
                        suppressContextMenu={true}
                        pinnedTopRowData={createTopData()}
                        noRowsOverlayComponentFramework={() => <div>No records found...</div>}
                        onGridReady={onGridReady}
                        onFirstDataRendered={onFirstDataRendered}
                        onSortChanged={(e: SortChangedEvent) => {
                            e.api.refreshCells();
                        }}
                        getRowStyle={(props: any) => {
                            if (props.node.rowPinned) {
                                return { backgroundColor: 'lightblue', fontWeight: 700 } as any;
                            }
                        }}
                    />
                </DetailReportWrapper>

                <div>{filteredData.length} items...</div>

                <CcbPayNowForm data={props.data} onMarkPaid={props.onMarkPaid} />
            </Modal.Body>
            <Modal.Footer>
                <Button onClick={props.onClose}>Close</Button>
            </Modal.Footer>
        </Modal>
    );
}

export default ApDetail